// File: frontend/src/contexts/AuthContext.jsx
// Authentication context for WA Contact Center CRM

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
        user: null,
        token: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        error: null,
        loading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
};

// Initial state
// Start in `loading: true` if there is a persisted token: we must verify it
// against the backend before deciding the user is authenticated. This avoids
// the "phantom logged-in" flash + 401 bounce on refresh.
const persistedToken = localStorage.getItem('wa-crm-token');
const initialState = {
  user: null,
  token: persistedToken,
  loading: !!persistedToken,
  error: null,
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verify token validity
  const verifyToken = async (token) => {
    try {
      // Set token in headers before making request
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get('/auth/me');

      const freshUser = response.data.data.user;
      // Mantener el cache local al día para la rehidratación offline de abajo.
      localStorage.setItem('wa-crm-user', JSON.stringify(freshUser));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: freshUser,
          token: token,
        },
      });
    } catch (error) {
      console.error('❌ Token verification failed:', error.response?.status || error.message);

      // Only log out on a genuine auth failure (401/403). On a transient
      // network error or 5xx backend blip at startup, KEEP the token so a
      // valid user isn't bounced — the ProtectedRoute / next request will
      // re-validate once the backend recovers.
      const isAuthFailure =
        error.response?.status === 401 || error.response?.status === 403;

      if (isAuthFailure) {
        // Clear invalid token and let the app render the login flow.
        localStorage.removeItem('wa-crm-token');
        delete api.defaults.headers.common['Authorization'];
        dispatch({ type: 'LOGOUT' });
      } else {
        // Fallo transitorio (red/5xx): sin esto, quedaba token presente pero
        // user=null y loading=false, y ProtectedRoute rebotaba a /login pese a
        // tener sesión válida. Rehidratar con el usuario cacheado del último
        // login; si el token fuera realmente inválido, la próxima llamada API
        // devuelve 401 y el interceptor cierra la sesión.
        try {
          const cachedUser = JSON.parse(localStorage.getItem('wa-crm-user'));
          if (cachedUser?.id) {
            dispatch({ type: 'LOGIN_SUCCESS', payload: { user: cachedUser, token } });
          }
        } catch (_) {
          // Cache corrupto: se ignora y el usuario verá el login.
        }
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // AUTH HYDRATION ON APP START
  // Always verify the persisted token against the backend before treating the
  // user as authenticated. If the token is missing/expired/invalid, verifyToken
  // clears the session and dispatches LOGOUT cleanly (no phantom logged-in flash,
  // no first-request-401 bounce). The real user is loaded from /auth/me, so we
  // never inject a fake fallback user.
  useEffect(() => {
    const token = localStorage.getItem('wa-crm-token');

    if (token) {
      // Set token in axios headers immediately so verifyToken's request is authed.
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken(token);
    } else {
      // No token: make sure we are not stuck in a loading state.
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []); // Keep empty dependency array to only run on mount

  // Login function
  const login = async (email, password) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await api.post('/auth/login', { email, password });

      const { token, user } = response.data.data;

      try {
        localStorage.setItem('wa-crm-token', token);
        localStorage.setItem('wa-crm-user', JSON.stringify(user));
        localStorage.setItem('wa-crm-timestamp', new Date().toISOString());
      } catch (storageError) {
        // localStorage lleno/bloqueado: la sesión funciona en memoria igual.
        console.error('❌ No se pudo persistir la sesión:', storageError);
      }

      // Set token in axios headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });

      toast.success(`Bem-vindo, ${user.first_name}!`);

      return { success: true };

    } catch (error) {
      console.error('❌ Login error:', error.response?.status || error.message);

      const errorMessage = error.response?.data?.error || 'Erro ao fazer login';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Google OAuth login - Redirects to backend OAuth flow
  const loginWithGoogle = () => {
    try {
      console.log('🔐 Initiating Google OAuth login');

      // Get backend URL (mirror api.js: en prod el frontend se sirve desde el mismo
      // origen que el backend, así que usamos window.location.origin; en dev, localhost)
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const backendUrl = isLocal
        ? (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000')
        : window.location.origin;
      const googleAuthUrl = `${backendUrl}/api/auth/google`;

      console.log('🔗 Redirecting to:', googleAuthUrl);

      // Redirect to backend Google OAuth endpoint
      window.location.href = googleAuthUrl;

    } catch (error) {
      console.error('❌ Error initiating Google OAuth:', error);
      toast.error('Erro ao iniciar login com Google');
    }
  };

  // Handle Google OAuth callback (called from callback page)
  const handleGoogleCallback = (token) => {
    try {
      console.log('🔐 Processing Google OAuth callback');
      console.log('💾 Storing token from Google OAuth');

      // Clear existing data
      localStorage.removeItem('wa-crm-token');
      localStorage.removeItem('wa-crm-user');
      localStorage.removeItem('wa-crm-timestamp');

      // Store token
      localStorage.setItem('wa-crm-token', token);
      localStorage.setItem('wa-crm-timestamp', new Date().toISOString());

      // Set token in axios headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Verify token and load user data
      verifyToken(token);

      toast.success('Login com Google realizado com sucesso!');

      return { success: true };

    } catch (error) {
      console.error('❌ Error handling Google callback:', error);
      toast.error('Erro ao processar login do Google');
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('wa-crm-token');
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ type: 'LOGOUT' });
      toast.success('Logout realizado com sucesso');
      
      // Redirect to login page
      setTimeout(() => {
        window.location.href = '/login';
      }, 500);
    }
  };

  // Update user profile
  const updateUser = (userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  // Check if user has specific permission
  const hasPermission = (resource, action) => {
    if (!state.user || !state.user.role) return false;
    
    // Get role name (handle both string and object formats)
    const roleName = typeof state.user.role === 'string' ? state.user.role : state.user.role.name;
    
    // Super Admin / Administrator have all permissions (real DB role names)
    if (roleName === 'Super_Admin' || roleName === 'administrator') return true;

    // For now, we'll use a basic role-based permission system
    // Later this can be expanded to use the permissions from the backend.
    // Role names here MUST match the real DB roles aligned with the backend.
    const rolePermissions = {
      'Admin_Marketing': {
        leads: ['read', 'create', 'update', 'delete'],
        contacts: ['read', 'create', 'update', 'delete'],
        companies: ['read', 'create', 'update', 'delete'],
        opportunities: ['read', 'create', 'update', 'delete'],
        activities: ['read', 'create', 'update', 'delete'],
        reports: ['read'],
        users: ['read', 'create', 'update', 'delete'],
        settings: ['read', 'update']
      },
      'Marketing_Digital': {
        leads: ['read', 'create', 'update'],
        contacts: ['read', 'create', 'update'],
        companies: ['read', 'create', 'update'],
        opportunities: ['read', 'create', 'update'],
        activities: ['read', 'create', 'update'],
        reports: ['read'],
        settings: ['read']
      },
      'Especialista_Leads': {
        leads: ['read', 'create', 'update'],
        contacts: ['read', 'create', 'update'],
        companies: ['read'],
        opportunities: ['read', 'create', 'update'],
        activities: ['read', 'create', 'update'],
        reports: ['read'],
        settings: ['read']
      },
      'Gerente_Vendas': {
        leads: ['read', 'create', 'update'],
        contacts: ['read', 'create', 'update'],
        companies: ['read', 'create', 'update'],
        opportunities: ['read', 'create', 'update'],
        activities: ['read', 'create', 'update'],
        reports: ['read'],
        users: ['read'],
        settings: ['read']
      },
      'Vendedor': {
        leads: ['read'],
        contacts: ['read', 'create', 'update'],
        companies: ['read'],
        opportunities: ['read', 'create', 'update'],
        activities: ['read', 'create', 'update'],
        reports: ['read'],
        settings: ['read']
      }
    };
    
    const userPermissions = rolePermissions[roleName];
    if (!userPermissions || !userPermissions[resource]) return false;
    
    return userPermissions[resource].includes(action);
  };

  // Check if user has specific role
  const hasRole = (roleName) => {
    const userRoleName = typeof state.user?.role === 'string' ? state.user.role : state.user?.role?.name;
    return userRoleName === roleName;
  };

  // Check if user can access resource based on scope
  const canAccess = (resource, targetUserId = null) => {
    if (!state.user) return false;
    
    // Get role name (handle both string and object formats)
    const roleName = typeof state.user.role === 'string' ? state.user.role : state.user.role?.name;
    
    // Super Admin, Administrator and Marketing admin have global access
    if (roleName === 'Super_Admin' || roleName === 'administrator' || roleName === 'Admin_Marketing') {
      return true;
    }

    // Sales Manager has team access
    if (roleName === 'Gerente_Vendas') {
      return true; // For now, allow access to all team data
    }

    // Regular users can only access their own data
    if (roleName === 'Vendedor' || roleName === 'Especialista_Leads' || roleName === 'Marketing_Digital') {
      return !targetUserId || targetUserId === state.user.id;
    }

    return false;
  };

  const value = {
    ...state,
    login,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    updateUser,
    hasPermission,
    hasRole,
    canAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};